import { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'

import { selectErrorsForKeys } from '@/reducers/fetch'
import {
  validateResetPasswordToken,
  updateResetPassword,
  UPDATE_RESET_PASSWORD_KEY,
  VALIDATE_RESET_PASSWORD_TOKEN_ERROR_KEY,
} from '@/actions/authActions'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import { Alert, Button, Input, Text, Card } from '@/ui'

export const ResetPasswordForm: React.FC = () => {
  const { token } = useParams<{ token?: string }>()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordMatch, setPasswordMatch] = useState(true)
  const dispatch = useAppDispatch()
  const auth = useReselector((state) => state.auth.security)

  const errors = useReselector((state) =>
    selectErrorsForKeys(state, [
      UPDATE_RESET_PASSWORD_KEY,
      VALIDATE_RESET_PASSWORD_TOKEN_ERROR_KEY,
    ])
  )

  useEffect(() => {
    if (token) {
      dispatch(validateResetPasswordToken(token))
    }
  }, [dispatch, token])

  const handleNewPassword = (val: string) => {
    setNewPassword(val)
    setPasswordMatch(val === confirmPassword && val.length > 0)
  }

  const handleConfirmPassword = (val: string) => {
    setConfirmPassword(val)
    setPasswordMatch(val === newPassword && val.length > 0)
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (token) {
      dispatch(updateResetPassword({ password: newPassword }, token))
    }
  }

  if (auth.isAuthenticated) {
    return <Navigate to="/dashboard" />
  }

  if (auth.validatingTokenLoading) {
    return (
      <div className="container mx-auto px-4">
        <Text variant={TypographyStyles.BODY_MD}>Loading...</Text>
      </div>
    )
  }

  if (auth.updatedPassword) {
    return (
      <div>
        <Navigate to="/login" />
        <Text variant={TypographyStyles.BODY_MD}>
          Redirecting you to login...
        </Text>
      </div>
    )
  }

  if (auth.validResetPasswordToken) {
    return (
      <div className="container mx-auto px-4 finances reset">
        <div className="flex flex-col items-center">
          <div className="w-full text-center mb-6">
            <Text variant={TypographyStyles.H2}>Reset Your Password Form</Text>
          </div>

          <div className="w-full md:w-3/4 lg:w-1/2 xl:w-2/5">
            <Card>
              <form onSubmit={handleSubmit} className="space-y-6">
                {Boolean(errors.length) && (
                  <>
                    {errors.map((error) => (
                      <div key={error.message} className="space-y-s">
                        <Alert variant="error">{error.message}</Alert>
                      </div>
                    ))}
                  </>
                )}

                <Input
                  placeholder="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => handleNewPassword(e.target.value)}
                />

                <Input
                  placeholder="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => handleConfirmPassword(e.target.value)}
                  error={!passwordMatch}
                />

                <div className="flex justify-center">
                  <Button
                    id="btn-auth-reset-password"
                    type="submit"
                    disabled={!passwordMatch}
                  >
                    Update Password
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 finances reset">
      <Text variant={TypographyStyles.H1}>
        Uh oh! It looks like your password reset link is no longer valid.
      </Text>

      <Text variant={TypographyStyles.BODY_MD}>
        If you need to reset your password, please head to the{' '}
        <Link to="/login">login</Link> page to try again.
      </Text>
    </div>
  )
}
